import { useEffect } from "react";

export interface OAuthResult {
  status: string;
  message: string;
  systemAuthorizationId?: number;
}

/**
 * In a component that handles OAuth callback params (status/message),
 * call this hook to detect if the window was opened as a popup.
 * If so, it forwards the result to the opener and closes the popup.
 *
 * Returns true if the current window is an OAuth popup (so the caller
 * can skip its normal callback handling).
 */
export function useOAuthPopupCallback(): boolean {
  const params = new URLSearchParams(window.location.search);
  const isPopup = !!window.opener && params.has("status");

  useEffect(() => {
    if (!isPopup) return;
    const systemAuthorizationId = params.get("system_authorization_id");
    window.opener.postMessage(
      {
        type: "oauth-callback",
        status: params.get("status"),
        message: params.get("message"),
        systemAuthorizationId: systemAuthorizationId
          ? Number(systemAuthorizationId)
          : undefined,
      },
      window.location.origin,
    );
    window.close();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- listing this would re-run the effect on changes it should ignore
  }, [isPopup]);

  return isPopup;
}

/**
 * Open the Exact Online OAuth flow in a centered popup window.
 * Returns a promise that resolves with { status, message, systemAuthorizationId } when the
 * popup completes, or rejects if the popup is blocked or closed early.
 */
export function openOAuthPopup(
  url: string,
): Promise<OAuthResult> {
  return new Promise((resolve, reject) => {
    const width = 600;
    const height = 700;
    const left = window.screenX + (window.innerWidth - width) / 2;
    const top = window.screenY + (window.innerHeight - height) / 2;

    const popup = window.open(
      url,
      "exactonline-oauth",
      `width=${width},height=${height},left=${left},top=${top}`,
    );

    if (!popup) {
      reject(new Error("Popup geblokkeerd door de browser"));
      return;
    }

    const cleanup = () => {
      window.removeEventListener("message", handleMessage);
      clearInterval(pollTimer);
    };

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== "oauth-callback") return;
      cleanup();
      resolve({
        status: event.data.status,
        message: event.data.message,
        systemAuthorizationId: event.data.systemAuthorizationId,
      });
    };

    window.addEventListener("message", handleMessage);

    // Detect if popup was closed without completing OAuth
    const pollTimer = setInterval(() => {
      if (popup.closed) {
        cleanup();
        reject(new Error("Authenticatie popup gesloten"));
      }
    }, 500);
  });
}
